import { GrowthBook } from '@growthbook/growthbook-react';
import { useEffect, useRef } from 'react';
import { pageViewEvent } from '@/modules/profile/events/pageView';

/**
 * Evaluate a feature as if the GrowthBook `slug` attribute were this doctor's slug, without touching
 * the shared instance's attributes (other code — growthbookGlobalContext — rewrites `slug` when the
 * user loads). A throwaway instance with the same definitions + attributes; no tracking callback, so
 * evaluating it has no side effects.
 */
export function isFeatureOnForSlug(gb: GrowthBook, key: string, slug: string | undefined): boolean {
  const scoped = new GrowthBook({
    enabled: (gb as any).context?.enabled !== false,
    attributes: { ...gb.getAttributes(), slug },
    features: gb.getFeatures(),
  });
  try {
    return scoped.isOn(key);
  } finally {
    scoped.destroy();
  }
}

/** pageViewEvent's input; `features` without `ravi_show_external_rate` (the hook evaluates it). */
type PageViewInput = Omit<Parameters<typeof pageViewEvent>[0], 'features'> & {
  features: Record<string, unknown>;
};

/**
 * Sends the doctor profile page view exactly once per (slug, doctor id) visit, once GrowthBook
 * definitions are loaded, the user info has settled, and the progressive data is complete.
 * `ravi_show_external_rate` is evaluated for this doctor's slug directly, so the event no longer
 * depends on the global `slug` attribute being intact in that commit.
 */
export function useDoctorPageView({
  growthbook,
  ready,
  userPending,
  progressivePending,
  input,
  informationDep,
}: {
  growthbook: GrowthBook | undefined;
  ready: boolean;
  userPending: boolean;
  progressivePending: boolean;
  input: PageViewInput | null;
  /** re-run trigger for the profile data (id on the progressive shell, the object on legacy) */
  informationDep: unknown;
}) {
  const sentRef = useRef<string | null>(null);
  const inputRef = useRef(input);
  inputRef.current = input;
  const slug = input?.slug;
  const doctorId = input?.information?.id;

  useEffect(() => {
    const current = inputRef.current;
    if (progressivePending || !current?.information || !growthbook || !ready || userPending) return;
    const key = `${slug}:${doctorId}`;
    if (sentRef.current === key) return;
    sentRef.current = key;
    pageViewEvent({
      ...current,
      features: {
        ravi_show_external_rate: isFeatureOnForSlug(growthbook, 'ravi_show_external_rate', slug),
        ...current.features,
      },
    });
  }, [growthbook, ready, userPending, progressivePending, informationDep, slug, doctorId]);
}
